import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useAuthStore } from './store/auth';
import { authApi } from './lib/api';
import { Layout } from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { InitPage } from './pages/InitPage';
import { DashboardV2Page } from './pages/DashboardV2Page';
import { OrdersPage } from './pages/OrdersPage';
import { ShipmentsPage } from './pages/ShipmentsPage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { StatusManagementPage } from './pages/StatusManagementPage';
import { AccountPage } from './pages/AccountPage';
import { SettingsPage } from './pages/SettingsPage';
import { AutomationsPage } from './pages/AutomationsPage';
import { WarehousePage } from './pages/WarehousePage';
import { UsersPage } from './pages/UsersPage';
import { AdminPanelPage } from './pages/AdminPanelPage';
import { ResendPage } from './pages/ResendPage';
import { CustomerPortalPage } from './pages/CustomerPortalPage';
import { ComplaintsPage } from './pages/ComplaintsPage';
import { FinanceDashboardPage } from './pages/FinanceDashboardPage';
import { FinanceCashflowPage } from './pages/FinanceCashflowPage';
import { FinanceCostsPage } from './pages/FinanceCostsPage';
import { FinanceTaxesPage } from './pages/FinanceTaxesPage';
import { FinanceCalculatorPage } from './pages/FinanceCalculatorPage';
import { FinanceRulesPage } from './pages/FinanceRulesPage';
import { FinanceSettingsPage } from './pages/FinanceSettingsPage';
import { SuperAdminTaxSettingsPage } from './pages/SuperAdminTaxSettingsPage';
import { MessagesInboxPage } from './pages/MessagesInboxPage';
import { MessagesSettingsPage } from './pages/MessagesSettingsPage';
import { KnowledgeBasePage } from './pages/KnowledgeBasePage';
import { AiManagementPage } from './pages/AiManagementPage';
import { SuperAdminAiPage } from './pages/SuperAdminAiPage';
import { SuperAdminFinanceCollectorPage } from './pages/SuperAdminFinanceCollectorPage';
import { KreatorPage } from './pages/KreatorPage';
import { HunterProductsPage } from './pages/hunter/HunterProductsPage';
import { LabProductListPage } from './pages/LabProductListPage';
import { LabAvatarsPage } from './pages/LabAvatarsPage';
import { LabVoicesPage } from './pages/LabVoicesPage';
import { LabProductDashboardPage } from './pages/LabProductDashboardPage';
import { StudioPage } from './pages/studio/StudioPage';
import { StudioEditorPage } from './pages/studio/StudioEditorPage';
import { MarketingLayoutPage } from './pages/marketing/MarketingLayoutPage';
import { MarketingCenterPage } from './pages/marketing/MarketingCenterPage';
import { MarketingEmailPage } from './pages/marketing/MarketingEmailPage';
// LabKnowledgeDetailPage removed — KB editing is now inline in dashboard
// Old content detail pages removed — content editing is inline in dashboard
// LabWorkspacePage removed — consolidated into LabProductDashboardPage
import { ToastProvider } from './components/ui/Toast';
import { FullscreenProvider } from './contexts/FullscreenContext';

const MarketingFlowEditorPage = lazy(async () => {
  const module = await import('./pages/marketing/flow-editor/MarketingFlowEditorPage');
  return { default: module.MarketingFlowEditorPage };
});

const MarketingMessageEditorPage = lazy(async () => {
  const module = await import('./pages/marketing/message-editor/MarketingMessageEditorPage');
  return { default: module.MarketingMessageEditorPage };
});

function OrderLinkRedirect() {
  const { orderId } = useParams();
  return <Navigate to={`/orders?id=${encodeURIComponent(orderId || '')}`} replace />;
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return !isAuthenticated ? <>{children}</> : <Navigate to="/" replace />;
}

// Placeholder pages for routes not yet implemented
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-text-primary mb-2">{title}</h1>
        <p className="text-text-secondary">Ta funkcjonalność będzie wkrótce dostępna.</p>
      </div>
    </div>
  );
}

function LegacyLabProductRedirect() {
  const { id, productId } = useParams();
  const location = useLocation();
  const targetId = id || productId || new URLSearchParams(location.search).get('product');
  return <Navigate to={{ pathname: targetId ? `/marketing/laboratorium/produkt/${targetId}` : '/marketing/laboratorium', search: location.search, hash: location.hash }} replace />;
}

function LegacyLaboratoryRedirect() {
  const location = useLocation();
  const pathname = location.pathname.replace(/^\/laboratorium/, '/marketing/laboratorium');
  return <Navigate to={{ pathname, search: location.search, hash: location.hash }} replace />;
}

export default function App() {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const syncSession = useAuthStore(state => state.syncSession);
  const [sessionReady, setSessionReady] = useState(!isAuthenticated);

  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated) {
      setSessionReady(true);
      return () => { cancelled = true; };
    }

    setSessionReady(false);
    authApi.me()
      .then(({ data }) => {
        if (!cancelled) syncSession(data.user, data.tenant);
      })
      .catch(() => {
        // Global 401 handling clears an actually expired cookie session.
      })
      .finally(() => {
        if (!cancelled) setSessionReady(true);
      });
    return () => { cancelled = true; };
  }, [isAuthenticated, syncSession]);

  if (!sessionReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-base text-text-muted">
        Sprawdzam sesję...
      </div>
    );
  }

  return (
    <ToastProvider>
    <FullscreenProvider>
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/init"
        element={
          <PublicRoute>
            <InitPage />
          </PublicRoute>
        }
      />
      {/* Public routes - no auth */}
      <Route path="/:storeSlug/panel/:token" element={<CustomerPortalPage />} />
      <Route path="/panel/:token" element={<CustomerPortalPage />} />
      <Route path="/:storeSlug/resend/:token" element={<ResendPage />} />
      <Route path="/resend/:token" element={<ResendPage />} />

      <Route
        path="/"
        element={
          <PrivateRoute>
            <ErrorBoundary>
              <Layout />
            </ErrorBoundary>
          </PrivateRoute>
        }
      >
        <Route index element={<DashboardV2Page />} />
        <Route path="dashboard" element={<DashboardV2Page />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="orders/:orderId" element={<OrderLinkRedirect />} />
        <Route path="warehouse" element={<WarehousePage />} />
        <Route path="complaints" element={<ComplaintsPage />} />
        <Route path="shipments" element={<ShipmentsPage />} />
        <Route path="invoices" element={<PlaceholderPage title="Faktury" />} />
        <Route path="integrations/*" element={<IntegrationsPage />} />
        <Route path="automations/*" element={<AutomationsPage />} />

        {/* Finance routes */}
        <Route path="finance" element={<FinanceDashboardPage />} />
        <Route path="finance/cashflow" element={<FinanceCashflowPage />} />
        <Route path="finance/costs" element={<FinanceCostsPage />} />
        <Route path="finance/taxes" element={<FinanceTaxesPage />} />
        <Route path="finance/calculator" element={<FinanceCalculatorPage />} />
        <Route path="finance/rules" element={<FinanceRulesPage />} />
        <Route path="finance/settings" element={<FinanceSettingsPage />} />

        {/* Messages routes */}
        <Route path="messages" element={<MessagesInboxPage />} />
        <Route path="messages/settings/*" element={<MessagesSettingsPage />} />
        <Route path="messages/knowledge-base" element={<KnowledgeBasePage />} />

        {/* Kreator routes */}
        <Route path="kreator/*" element={<KreatorPage />} />

        {/* Marketing routes */}
        <Route path="marketing" element={<MarketingLayoutPage />}>
          <Route index element={<MarketingCenterPage />} />

          {/* Baza produktów i Laboratorium marek */}
          <Route path="baza-produktow" element={<HunterProductsPage />} />
          <Route path="laboratorium" element={<Navigate to="/marketing/baza-produktow?view=laboratory" replace />} />
          <Route path="laboratorium/marka/:brandId" element={<LabProductListPage />} />
          <Route path="laboratorium/bez-marki" element={<LabProductListPage />} />
          <Route path="laboratorium/avatary" element={<LabAvatarsPage />} />
          <Route path="laboratorium/glosy" element={<LabVoicesPage />} />
          <Route path="laboratorium/produkt/:productId" element={<LabProductDashboardPage />} />
          <Route path="laboratorium/produkty" element={<Navigate to="/marketing/baza-produktow" replace />} />
          <Route path="laboratorium/produkty/:id" element={<LegacyLabProductRedirect />} />
          <Route path="laboratorium/:productId" element={<LegacyLabProductRedirect />} />

          {/* Starsze adresy widoków Laboratorium */}
          <Route path="laboratorium/dashboard/:productId" element={<LegacyLabProductRedirect />} />
          <Route path="laboratorium/knowledge/:productId/:kbId" element={<LegacyLabProductRedirect />} />
          <Route path="laboratorium/knowledge/:productId" element={<LegacyLabProductRedirect />} />
          <Route path="laboratorium/landings/:productId/:contentId" element={<LegacyLabProductRedirect />} />
          <Route path="laboratorium/listicales/:productId/:contentId" element={<LegacyLabProductRedirect />} />
          <Route path="laboratorium/advertoriales/:productId/:contentId" element={<LegacyLabProductRedirect />} />
          <Route path="laboratorium/quizy/:productId/:contentId" element={<LegacyLabProductRedirect />} />
          <Route path="laboratorium/knowledge" element={<LegacyLabProductRedirect />} />
          <Route path="laboratorium/landings" element={<LegacyLabProductRedirect />} />
          <Route path="laboratorium/listicales" element={<LegacyLabProductRedirect />} />
          <Route path="laboratorium/advertoriales" element={<LegacyLabProductRedirect />} />
          <Route path="laboratorium/quizy" element={<LegacyLabProductRedirect />} />
          <Route path="laboratorium/products/:id" element={<LegacyLabProductRedirect />} />

          {/* Email */}
          <Route path="email" element={<MarketingEmailPage view="overview" />} />
          <Route path="email/wiadomosci" element={<MarketingEmailPage view="messages" />} />
          <Route
            path="email/wiadomosci/edytor"
            element={(
              <Suspense fallback={<div className="flex min-h-[50vh] items-center justify-center text-sm text-text-muted">Ładowanie kompozytora...</div>}>
                <MarketingMessageEditorPage />
              </Suspense>
            )}
          />
          <Route path="email/automatyzacje" element={<MarketingEmailPage view="automations" />} />
          <Route
            path="email/automatyzacje/edytor"
            element={(
              <Suspense fallback={<div className="flex min-h-[50vh] items-center justify-center text-sm text-text-muted">Ładowanie buildera flow...</div>}>
                <MarketingFlowEditorPage />
              </Suspense>
            )}
          />
          <Route path="email/wysylki" element={<MarketingEmailPage view="sends" />} />
          <Route path="email/odbiorcy" element={<MarketingEmailPage view="recipients" />} />
          <Route path="email/analityka" element={<MarketingEmailPage view="analytics" />} />
          <Route path="email/ustawienia" element={<MarketingEmailPage view="settings" />} />
        </Route>

        {/* Stare adresy Laboratorium pozostają bezpiecznymi przekierowaniami. */}
        <Route path="laboratorium" element={<Navigate to="/marketing/laboratorium" replace />} />
        <Route path="laboratorium/*" element={<LegacyLaboratoryRedirect />} />

        {/* Studio routes */}
        <Route path="studio" element={<StudioPage />} />
        <Route path="studio/:projectId" element={<StudioEditorPage />} />

        {/* AI Management (user dropdown) */}
        <Route path="ai" element={<AiManagementPage />} />

        {/* Super Admin routes */}
        <Route path="super-admin/tax-settings" element={<SuperAdminTaxSettingsPage />} />
        <Route path="super-admin/ai" element={<SuperAdminAiPage />} />
        <Route path="super-admin/finance-collector" element={<SuperAdminFinanceCollectorPage />} />

        {/* Admin routes */}
        <Route path="admin/statuses" element={<StatusManagementPage />} />
        <Route path="admin/users" element={<UsersPage />} />
        <Route path="admin/settings/*" element={<SettingsPage />} />
        <Route path="admin/panel" element={<AdminPanelPage />} />

        {/* Account */}
        <Route path="account" element={<AccountPage />} />
      </Route>
    </Routes>
    </FullscreenProvider>
    </ToastProvider>
  );
}
